import { useEffect } from "react";
import { useLocation } from "react-router";
import { useMotionPreference } from "./MotionPreference";

/** Observe new cards once; DOM changes are batched instead of rescanning every list. */
export const useRevealMotion = () => {
  const { pathname } = useLocation();
  const [motion] = useMotionPreference();
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const compact = window.matchMedia("(max-width: 767px)").matches;
    let cleanup = () => {};
    const start = () => {
      cleanup();
      if (
        motion === "off" ||
        media.matches ||
        !("IntersectionObserver" in window)
      )
        return;
      const root = document.getElementById("root");
      if (!root) return;
      const selector =
        ".rovero-page [data-slot='card'], .rovero-page .rovero-contact-capsule, .rovero-page [data-motion-item], .rovero-page [data-slot='table-body'] [data-slot='table-row']" +
        (compact ? ", .rovero-page [data-mobile-reveal]" : "");
      const seen = new Set<HTMLElement>();
      const observer = new IntersectionObserver(
        (entries) => {
          entries
            .filter((entry) => entry.isIntersecting)
            .forEach((entry, index) => {
              const element = entry.target as HTMLElement;
              element.style.setProperty(
                "--reveal-delay",
                `${Math.min(index, 5) * (compact ? 35 : 65)}ms`,
              );
              element.dataset.reveal = "visible";
              observer.unobserve(element);
            });
        },
        { threshold: 0 },
      );
      const observe = (element: HTMLElement) => {
        if (seen.has(element) || !element.isConnected) return;
        // A card inside another animated card should not move twice.
        if (compact && element.parentElement?.closest(selector)) return;
        seen.add(element);
        element.dataset.reveal = "pending";
        observer.observe(element);
      };
      const scan = (node: Element) => {
        if (node.closest(".crm-calendar-shell")) return;
        if (node instanceof HTMLElement && node.matches(selector))
          observe(node);
        node.querySelectorAll<HTMLElement>(selector).forEach(observe);
      };
      let frame = 0;
      const pending = new Set<Element>();
      const mutations = new MutationObserver((records) => {
        for (const record of records)
          for (const node of record.addedNodes)
            if (node instanceof Element) pending.add(node);
        if (pending.size && !frame)
          frame = requestAnimationFrame(() => {
            frame = 0;
            pending.forEach((node) => {
              if (node.isConnected) scan(node);
            });
            pending.clear();
          });
      });
      mutations.observe(root, { childList: true, subtree: true });
      scan(root);
      cleanup = () => {
        observer.disconnect();
        mutations.disconnect();
        cancelAnimationFrame(frame);
        pending.clear();
        seen.forEach((element) => {
          delete element.dataset.reveal;
          element.style.removeProperty("--reveal-delay");
        });
      };
    };
    start();
    media.addEventListener("change", start);
    return () => {
      cleanup();
      media.removeEventListener("change", start);
    };
  }, [motion, pathname]);
};
